import type { BlockNode, LibraryBlock } from "../build";
import {
  CARD_SHADOW,
  ellipse,
  icon,
  motion,
  pill,
  rect,
  SHADOW,
  stack,
  type TemplateNode,
  text,
  WHITE,
} from "./helpers";

export const CARDS: LibraryBlock[] = [
  {
    id: "karte-tipp",
    title: "Tipp-Karte",
    category: "karte",
    width: 960,
    height: 330,
    anchor: "top",
    duration: 5000,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 960,
        h: 330,
        fill: "background/95",
        radius: "lg",
        name: "Karte",
        ...motion("slide-down"),
      }),
      ...pill(40, 40, 240, 64, "TIPP #1", "primary", 28, motion("pop", { delay: 200 })),
      text({
        text: "So schneidest du Videos in drei Klicks",
        style: "heading-2",
        size: 58,
        color: "text",
        x: 40,
        y: 132,
        w: 880,
        ...motion("slide-up", { delay: 320 }),
      }),
    ],
  },
  {
    id: "karte-info",
    title: "Info-Box",
    category: "karte",
    width: 900,
    height: 240,
    anchor: "top",
    duration: 5000,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 900,
        h: 240,
        fill: "surface",
        radius: "md",
        name: "Karte",
        shadow: { color: "#00000026", y: 12, blur: 40 },
        ...motion("pop"),
      }),
      ellipse({
        x: 40,
        y: 70,
        w: 100,
        h: 100,
        fill: "accent",
        name: "Kreis",
        ...motion("zoom", { delay: 180 }),
      }),
      text({
        text: "i",
        style: "heading-1",
        size: 64,
        color: "on:accent",
        x: 40,
        y: 70,
        w: 100,
        h: 100,
        align: "center",
        ...motion("zoom", { delay: 180 }),
      }),
      text({
        text: "Gut zu wissen: Der Eintritt ist für Kinder bis 12 Jahre frei.",
        style: "body",
        size: 38,
        color: "text",
        x: 176,
        y: 56,
        w: 690,
        ...motion("fade", { delay: 300 }),
      }),
    ],
  },
  {
    id: "karte-liste",
    title: "Drei Punkte",
    category: "karte",
    width: 940,
    height: 560,
    anchor: "center",
    duration: 6000,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 940,
        h: 560,
        fill: "background/95",
        radius: "lg",
        name: "Karte",
        ...motion("slide-up"),
      }),
      text({
        text: "Darauf kommt es an",
        style: "heading-2",
        size: 60,
        color: "text",
        x: 50,
        y: 44,
        w: 840,
        ...motion("fade", { delay: 150 }),
      }),
      ...[0, 1, 2].flatMap((index): TemplateNode[] => {
        const y = 170 + index * 120;
        const delay = 400 + index * 350;
        const labels = ["Klares Ziel", "Kurze Wege", "Ehrliches Feedback"];
        return [
          ellipse({
            x: 50,
            y,
            w: 76,
            h: 76,
            fill: "primary",
            name: `Punkt ${index + 1}`,
            ...motion("pop", { delay }),
          }),
          text({
            text: String(index + 1),
            style: "heading-2",
            size: 40,
            color: "on:primary",
            x: 50,
            y,
            w: 76,
            h: 76,
            align: "center",
            ...motion("pop", { delay }),
          }),
          text({
            text: labels[index]!,
            style: "subline",
            size: 44,
            color: "text",
            x: 156,
            y,
            w: 740,
            h: 76,
            ...motion("slide-left", { delay: delay + 80 }),
          }),
        ];
      }),
    ],
  },
  {
    id: "karte-zitat",
    title: "Zitat",
    category: "karte",
    width: 940,
    height: 520,
    anchor: "center",
    duration: 6000,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 940,
        h: 520,
        fill: "surface",
        radius: "lg",
        name: "Karte",
        ...motion("fade"),
      }),
      text({
        text: "“",
        style: "quote",
        size: 240,
        color: "accent",
        x: 50,
        y: -20,
        w: 200,
        ...motion("drop", { delay: 150, duration: 700 }),
      }),
      text({
        text: "Wer aufhört, besser zu werden, hat aufgehört, gut zu sein.",
        style: "quote",
        size: 52,
        color: "text",
        x: 70,
        y: 150,
        w: 800,
        ...motion("fade", { delay: 350, duration: 700 }),
      }),
      text({
        text: "Philip Rosenthal",
        style: "subline",
        size: 32,
        color: "text-muted",
        x: 70,
        y: 420,
        w: 800,
        ...motion("slide-up", { delay: 700 }),
      }),
    ],
  },
  {
    id: "karte-zahl",
    title: "Große Zahl",
    category: "karte",
    width: 900,
    height: 420,
    anchor: "center",
    duration: 4500,
    nodes: [
      text({
        text: "87\u00a0%",
        style: "heading-1",
        size: 230,
        color: "accent",
        shadow: SHADOW,
        x: 0,
        y: 0,
        w: 900,
        align: "center",
        ...motion("pop", { duration: 600 }),
      }),
      text({
        text: "unserer Kundschaft empfiehlt uns weiter",
        style: "heading-2",
        size: 54,
        color: WHITE,
        shadow: SHADOW,
        x: 60,
        y: 270,
        w: 780,
        align: "center",
        ...motion("slide-up", { delay: 300 }),
      }),
    ],
  },
  {
    id: "karte-frage",
    title: "Frage an dich",
    category: "karte",
    width: 920,
    height: 380,
    anchor: "center",
    duration: 5000,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 920,
        h: 380,
        fill: "accent",
        radius: "lg",
        rotation: 1.5,
        name: "Karte",
        ...motion("drop", { duration: 800 }),
      }),
      text({
        text: "FRAGE AN DICH",
        style: "subline",
        size: 30,
        color: "on:accent/80",
        x: 50,
        y: 44,
        w: 820,
        rotation: 1.5,
        ...motion("fade", { delay: 500 }),
      }),
      text({
        text: "Was ist dein bester Tipp für den Montag?",
        style: "heading-2",
        size: 60,
        color: "on:accent",
        x: 50,
        y: 104,
        w: 820,
        rotation: 1.5,
        ...motion("fade", { delay: 600 }),
      }),
      text({
        text: "Schreib's in die Kommentare",
        style: "caption",
        size: 30,
        color: "on:accent/80",
        x: 50,
        y: 300,
        w: 820,
        rotation: 1.5,
        ...motion("fade", { delay: 800 }),
      }),
    ],
  },
  {
    id: "karte-wusstest-du",
    title: "Wusstest du?",
    category: "karte",
    width: 940,
    height: 330,
    anchor: "top",
    duration: 5500,
    nodes: [
      rect({
        x: 0,
        y: 40,
        w: 940,
        h: 290,
        fill: "#000000b3",
        radius: "lg",
        name: "Karte",
        ...motion("zoom"),
      }),
      ...pill(
        40,
        0,
        330,
        80,
        "WUSSTEST DU?",
        "accent",
        30,
        motion("drop", { delay: 150, duration: 700 }),
      ),
      text({
        text: "Ein Kolibri schlägt bis zu 80-mal pro Sekunde mit den Flügeln.",
        style: "heading-2",
        size: 54,
        color: WHITE,
        x: 50,
        y: 130,
        w: 840,
        ...motion("slide-up", { delay: 350 }),
      }),
    ],
  },
  {
    id: "karte-schritt",
    title: "Schritt",
    category: "karte",
    width: 900,
    height: 180,
    anchor: "top",
    duration: 4500,
    nodes: [
      rect({
        x: 90,
        y: 20,
        w: 810,
        h: 140,
        fill: "background/95",
        radius: "full",
        name: "Fläche",
        ...motion("slide-left", { delay: 120 }),
      }),
      ellipse({ x: 0, y: 0, w: 180, h: 180, fill: "primary", name: "Kreis", ...motion("spin") }),
      text({
        text: "1",
        style: "heading-1",
        size: 110,
        color: "on:primary",
        x: 0,
        y: 0,
        w: 180,
        h: 180,
        align: "center",
        ...motion("spin"),
      }),
      text({
        text: "Material bereitlegen",
        style: "heading-2",
        size: 52,
        color: "text",
        x: 220,
        y: 20,
        w: 640,
        h: 140,
        ...motion("slide-left", { delay: 220 }),
      }),
    ],
  },
  {
    id: "karte-vorher-nachher",
    title: "Vorher / Nachher",
    category: "karte",
    width: 1080,
    height: 1920,
    anchor: "fill",
    duration: 6000,
    nodes: ({ width, height }) => {
      const portrait = height >= width;
      const m = 60;
      const second = portrait ? { x: m, y: height / 2 + m } : { x: width / 2 + m, y: m };
      return [
        ...pill(
          m,
          m + (portrait ? 80 : 0),
          280,
          76,
          "VORHER",
          "#000000b3",
          30,
          motion("fly-right"),
          {
            color: WHITE,
          },
        ),
        ...pill(
          second.x,
          second.y,
          300,
          76,
          "NACHHER",
          "accent",
          30,
          motion("fly-right", { delay: 250 }),
        ),
      ];
    },
  },
  {
    id: "karte-checkliste",
    title: "Checkliste",
    category: "karte",
    tags: ["abhaken", "To-do", "Liste", "Packliste", "erledigt"],
    width: 900,
    height: 610,
    anchor: "center",
    duration: 6000,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 900,
        h: 610,
        fill: "background/95",
        radius: "lg",
        name: "Karte",
        ...motion("slide-up"),
      }),
      text({
        text: "Deine Packliste",
        style: "heading-2",
        size: 56,
        color: "text",
        x: 50,
        y: 44,
        w: 800,
        ...motion("fade", { delay: 150 }),
      }),
      ...["Sonnencreme", "Wanderschuhe", "Proviant", "Gute Laune"].flatMap(
        (label, index): BlockNode[] => {
          const y = 170 + index * 105;
          const delay = 300 + index * 450;
          return [
            rect({
              x: 50,
              y,
              w: 70,
              h: 70,
              stroke: { color: "primary", width: 6 },
              radius: "sm",
              name: "Kästchen",
              ...motion("pop", { delay }),
            }),
            icon({
              icon: "check",
              x: 58,
              y: y + 8,
              w: 54,
              h: 54,
              color: "primary",
              name: "Haken",
              ...motion("pop", { delay: delay + 300 }),
            }),
            text({
              text: label,
              style: "subline",
              size: 42,
              color: "text",
              x: 150,
              y,
              w: 700,
              h: 70,
              ...motion("slide-left", { delay }),
            }),
          ];
        },
      ),
    ],
  },
  {
    id: "karte-pro-contra",
    title: "Pro & Contra",
    category: "karte",
    tags: ["Vorteile", "Nachteile", "Vergleich", "Für und Wider", "Plus Minus"],
    width: 960,
    height: 420,
    anchor: "center",
    duration: 6000,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 960,
        h: 420,
        fill: "background/95",
        radius: "lg",
        name: "Karte",
        ...motion("pop"),
      }),
      rect({
        x: 479,
        y: 40,
        w: 2,
        h: 340,
        fill: "text-muted/30",
        name: "Trenner",
        ...motion("fade"),
      }),
      ...[
        {
          x: 40,
          icon: "check-circle" as const,
          color: "#16a34a",
          title: "PRO",
          items: ["Spart viel Zeit", "Günstig im Alltag"],
        },
        {
          x: 520,
          icon: "x-circle" as const,
          color: "#dc2626",
          title: "CONTRA",
          items: ["Braucht Einarbeitung", "Nicht überall verfügbar"],
        },
      ].flatMap((column, columnIndex): BlockNode[] => {
        const base = 200 + columnIndex * 900;
        return [
          icon({
            icon: column.icon,
            x: column.x,
            y: 40,
            w: 70,
            h: 70,
            color: column.color,
            name: column.title,
            ...motion("pop", { delay: base }),
          }),
          text({
            text: column.title,
            style: "heading-2",
            size: 46,
            color: "text",
            x: column.x + 90,
            y: 40,
            w: 310,
            h: 70,
            ...motion("slide-left", { delay: base }),
          }),
          ...column.items.map((item, index) =>
            text({
              text: `• ${item}`,
              style: "body",
              size: 36,
              color: "text",
              x: column.x,
              y: 150 + index * 120,
              w: 400,
              ...motion("slide-up", { delay: base + 250 + index * 250 }),
            }),
          ),
        ];
      }),
    ],
  },
  {
    id: "karte-zutaten",
    title: "Zutaten",
    category: "karte",
    tags: ["Rezept", "Kochen", "Backen", "Liste", "Einkaufsliste", "Food"],
    width: 900,
    height: 600,
    anchor: "center",
    duration: 6000,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 900,
        h: 600,
        fill: "background/95",
        radius: "lg",
        name: "Karte",
        ...motion("slide-up"),
      }),
      ellipse({
        x: 50,
        y: 44,
        w: 90,
        h: 90,
        fill: "accent",
        name: "Kreis",
        ...motion("pop", { delay: 150 }),
      }),
      icon({
        icon: "fork-knife",
        x: 70,
        y: 64,
        w: 50,
        h: 50,
        color: "on:accent",
        name: "Besteck",
        ...motion("pop", { delay: 150 }),
      }),
      text({
        text: "Zutaten für 4 Personen",
        style: "heading-2",
        size: 50,
        color: "text",
        x: 165,
        y: 44,
        w: 690,
        h: 90,
        ...motion("fade", { delay: 200 }),
      }),
      rect({
        x: 50,
        y: 166,
        w: 800,
        h: 3,
        fill: "text-muted/30",
        name: "Linie",
        ...motion("fade", { delay: 200 }),
      }),
      stack({
        x: 50,
        y: 196,
        w: 800,
        gap: 16,
        items: [
          "500 g Mehl",
          "300 ml lauwarmes Wasser",
          "1 Päckchen Trockenhefe",
          "2 TL Salz",
          "3 EL Olivenöl",
        ].map((item, index) => ({
          kind: "text" as const,
          text: `• ${item}`,
          style: "body" as const,
          size: 38,
          color: "text",
          ...motion("slide-left", { delay: 350 + index * 250 }),
        })),
      }),
    ],
  },
  {
    id: "karte-drei-schritte",
    title: "Drei Schritte",
    category: "karte",
    tags: ["Anleitung", "Ablauf", "Prozess", "1-2-3", "So geht's"],
    width: 960,
    height: 320,
    anchor: "center",
    duration: 5500,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 960,
        h: 320,
        fill: "background/95",
        radius: "lg",
        name: "Karte",
        ...motion("pop"),
      }),
      rect({
        x: 160,
        y: 86,
        w: 640,
        h: 8,
        fill: "surface",
        name: "Verbindung",
        ...motion("fade", { delay: 150 }),
      }),
      ...[
        { label: "Anmelden", note: "In einer Minute" },
        { label: "Auswählen", note: "Aus 200 Kursen" },
        { label: "Loslegen", note: "Sofort starten" },
      ].flatMap((step, index): BlockNode[] => {
        const center = 160 + index * 320;
        const delay = 250 + index * 400;
        return [
          ellipse({
            x: center - 65,
            y: 25,
            w: 130,
            h: 130,
            fill: "primary",
            name: `Schritt ${index + 1}`,
            ...motion("pop", { delay }),
          }),
          text({
            text: String(index + 1),
            style: "heading-1",
            size: 64,
            color: "on:primary",
            x: center - 65,
            y: 25,
            w: 130,
            h: 130,
            align: "center",
            ...motion("pop", { delay }),
          }),
          text({
            text: step.label,
            style: "subline",
            size: 36,
            color: "text",
            x: center - 150,
            y: 184,
            w: 300,
            align: "center",
            ...motion("slide-up", { delay: delay + 100 }),
          }),
          text({
            text: step.note,
            style: "caption",
            size: 26,
            color: "text-muted",
            x: center - 150,
            y: 240,
            w: 300,
            align: "center",
            ...motion("fade", { delay: delay + 200 }),
          }),
        ];
      }),
    ],
  },
  {
    id: "karte-top-3",
    title: "Top 3",
    category: "karte",
    tags: ["Ranking", "Rangliste", "Bestenliste", "Platz", "Favoriten"],
    width: 900,
    height: 550,
    anchor: "center",
    duration: 6000,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 900,
        h: 550,
        fill: "background/95",
        radius: "lg",
        name: "Karte",
        ...motion("slide-up"),
      }),
      text({
        text: "Unsere Top 3",
        style: "heading-2",
        size: 56,
        color: "text",
        x: 50,
        y: 40,
        w: 800,
        ...motion("fade", { delay: 150 }),
      }),
      ...["Zimtschnecke", "Apfelstrudel", "Käsekuchen"].flatMap((item, index): BlockNode[] => {
        const y = 150 + index * 125;
        // The countdown to number one: third place first.
        const delay = 400 + (2 - index) * 500;
        const badge = index === 0 ? "accent" : "primary";
        return [
          rect({
            x: 50,
            y,
            w: 800,
            h: 105,
            fill: "surface",
            radius: "md",
            name: `Platz ${index + 1}`,
            ...motion("slide-left", { delay }),
          }),
          ellipse({
            x: 70,
            y: y + 12,
            w: 80,
            h: 80,
            fill: badge,
            name: "Kreis",
            ...motion("pop", { delay: delay + 100 }),
          }),
          text({
            text: String(index + 1),
            style: "heading-2",
            size: 44,
            color: `on:${badge}`,
            x: 70,
            y: y + 12,
            w: 80,
            h: 80,
            align: "center",
            ...motion("pop", { delay: delay + 100 }),
          }),
          text({
            text: item,
            style: "subline",
            size: 40,
            color: "text",
            x: 180,
            y,
            w: 640,
            h: 105,
            ...motion("slide-left", { delay: delay + 50 }),
          }),
        ];
      }),
    ],
  },
  {
    id: "karte-mythos-fakt",
    title: "Mythos oder Fakt",
    category: "karte",
    tags: ["Irrtum", "Wahrheit", "Faktencheck", "Quiz", "stimmt das"],
    width: 920,
    height: 560,
    anchor: "center",
    duration: 6500,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 920,
        h: 250,
        fill: "background/95",
        radius: "lg",
        name: "Karte Mythos",
        ...motion("slide-up"),
      }),
      ...pill(40, 36, 230, 60, "MYTHOS", "#dc2626", 28, motion("pop", { delay: 200 }), {
        color: WHITE,
      }),
      text({
        text: "Spinat hat besonders viel Eisen.",
        style: "heading-2",
        size: 46,
        color: "text",
        x: 40,
        y: 116,
        w: 840,
        ...motion("fade", { delay: 350 }),
      }),
      rect({
        x: 0,
        y: 290,
        w: 920,
        h: 270,
        fill: "primary",
        radius: "lg",
        name: "Karte Fakt",
        ...motion("pop", { delay: 2000 }),
      }),
      ...pill(40, 326, 200, 60, "FAKT", "#16a34a", 28, motion("pop", { delay: 2150 }), {
        color: WHITE,
      }),
      text({
        text: "Ein Rechenfehler – andere Gemüse haben mehr.",
        style: "heading-2",
        size: 44,
        color: "on:primary",
        x: 40,
        y: 406,
        w: 840,
        ...motion("fade", { delay: 2300 }),
      }),
    ],
  },
  {
    id: "karte-termin",
    title: "Termin",
    category: "karte",
    tags: ["Event", "Veranstaltung", "Datum", "Kalender", "Einladung", "Save the Date"],
    width: 940,
    height: 280,
    anchor: "center",
    duration: 5500,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 940,
        h: 280,
        fill: "background/95",
        radius: "lg",
        shadow: CARD_SHADOW,
        name: "Karte",
        ...motion("pop"),
      }),
      rect({
        x: 30,
        y: 30,
        w: 220,
        h: 220,
        fill: "primary",
        radius: "md",
        name: "Datum",
        ...motion("zoom", { delay: 150 }),
      }),
      text({
        text: "12",
        style: "heading-1",
        size: 110,
        color: "on:primary",
        x: 30,
        y: 48,
        w: 220,
        align: "center",
        ...motion("zoom", { delay: 150 }),
      }),
      text({
        text: "JUNI",
        style: "subline",
        size: 34,
        color: "on:primary/80",
        x: 30,
        y: 182,
        w: 220,
        align: "center",
        ...motion("zoom", { delay: 150 }),
      }),
      text({
        text: "Sommerfest im Hof",
        style: "heading-2",
        size: 52,
        color: "text",
        x: 290,
        y: 40,
        w: 610,
        ...motion("slide-left", { delay: 300 }),
      }),
      text({
        text: "Sa · 15–20 Uhr · Eintritt frei",
        style: "subline",
        size: 30,
        color: "text-muted",
        x: 290,
        y: 118,
        w: 610,
        ...motion("slide-left", { delay: 400 }),
      }),
      icon({
        icon: "map-pin",
        x: 290,
        y: 184,
        w: 44,
        h: 44,
        color: "primary",
        name: "Pin",
        ...motion("fade", { delay: 500 }),
      }),
      text({
        text: "Marktstraße 5, Jena",
        style: "body",
        size: 30,
        color: "text",
        x: 346,
        y: 184,
        w: 554,
        h: 44,
        ...motion("fade", { delay: 500 }),
      }),
    ],
  },
  {
    id: "karte-profi-tipp",
    title: "Profi-Tipp",
    category: "karte",
    tags: ["Tipp", "Idee", "Glühbirne", "Trick", "Hack"],
    width: 940,
    height: 300,
    anchor: "top",
    duration: 5000,
    nodes: [
      rect({
        x: 0,
        y: 40,
        w: 940,
        h: 260,
        fill: "background/95",
        radius: "lg",
        name: "Karte",
        ...motion("slide-down"),
      }),
      ellipse({
        x: 40,
        y: 0,
        w: 110,
        h: 110,
        fill: "accent",
        name: "Kreis",
        ...motion("drop", { delay: 150, duration: 700 }),
      }),
      icon({
        icon: "lightbulb",
        x: 65,
        y: 25,
        w: 60,
        h: 60,
        color: "on:accent",
        name: "Glühbirne",
        ...motion("drop", { delay: 150, duration: 700, loop: "wiggle" }),
      }),
      text({
        text: "PROFI-TIPP",
        style: "subline",
        size: 30,
        color: "primary",
        x: 172,
        y: 58,
        w: 700,
        ...motion("fade", { delay: 300 }),
      }),
      text({
        text: "Fotografiere dein Essen bei Tageslicht am Fenster.",
        style: "heading-2",
        size: 46,
        color: "text",
        x: 40,
        y: 136,
        w: 860,
        ...motion("slide-up", { delay: 400 }),
      }),
    ],
  },
];
